/**
 * ShareModalMemberRow — renders a single member row in the sharing modal.
 *
 * Displays avatar (initials circle with color), display name, email,
 * role selector (admin) or static role badge (non-admin), and a remove button.
 *
 * Validates: Requirements 5.1, 5.6, 6.1, 6.6, 6.7, 7.1, 7.5, 10.4, 10.5
 */

import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { cn } from '../../lib/utils';
import { getInitials, getAvatarColor } from '../../utils/avatar';
import type { BoardMemberItem, BoardRole } from './sharing-types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface MemberRowProps {
  readonly member: BoardMemberItem;
  readonly isAdmin: boolean;
  readonly isLastAdmin: boolean;
  readonly onRoleChange: (userId: string, newRole: BoardRole) => void;
  readonly onRemove: (userId: string) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ROLE_BADGE_STYLES: Record<BoardRole, string> = {
  admin: 'bg-blue-100 text-blue-800 border-blue-200',
  normal: 'bg-green-100 text-green-800 border-green-200',
  'read-only': 'bg-gray-100 text-gray-700 border-gray-200',
};

const ROLE_LABELS: Record<BoardRole, string> = {
  admin: 'Admin',
  normal: 'Normal',
  'read-only': 'Read-only',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ShareModalMemberRow({
  member,
  isAdmin,
  isLastAdmin,
  onRoleChange,
  onRemove,
}: MemberRowProps): React.ReactElement {
  const initials = getInitials(member.displayName ?? '');
  const avatarColor = getAvatarColor(member.userId);

  return (
    <div className="flex items-center gap-3 px-2 py-2">
      {/* Avatar */}
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
        style={{ backgroundColor: avatarColor }}
        aria-hidden="true"
      >
        {initials}
      </div>

      {/* Name and email */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">
          {member.displayName}
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {member.email}
        </p>
      </div>

      {/* Role selector or static badge */}
      {isAdmin ? (
        <RoleSelector
          member={member}
          isLastAdmin={isLastAdmin}
          onRoleChange={onRoleChange}
        />
      ) : (
        <Badge
          className={cn('shrink-0', ROLE_BADGE_STYLES[member.role])}
        >
          {ROLE_LABELS[member.role]}
        </Badge>
      )}

      {/* Remove button (admin only, hidden for last admin) */}
      {isAdmin && !isLastAdmin && (
        <button
          type="button"
          aria-label={`Remove ${member.displayName} from board`}
          onClick={() => onRemove(member.userId)}
          className="shrink-0 rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <TrashIcon />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RoleSelector — Radix Select with tooltip for last admin
// ---------------------------------------------------------------------------

interface RoleSelectorProps {
  readonly member: BoardMemberItem;
  readonly isLastAdmin: boolean;
  readonly onRoleChange: (userId: string, newRole: BoardRole) => void;
}

function RoleSelector({
  member,
  isLastAdmin,
  onRoleChange,
}: RoleSelectorProps): React.ReactElement {
  const trigger = (
    <Select
      value={member.role}
      onValueChange={(value) => onRoleChange(member.userId, value as BoardRole)}
      disabled={isLastAdmin}
    >
      <SelectTrigger
        className="h-8 w-[120px] shrink-0 text-xs"
        aria-label={`Role for ${member.displayName}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="admin">Admin</SelectItem>
        <SelectItem value="normal">Normal</SelectItem>
        <SelectItem value="read-only">Read-only</SelectItem>
      </SelectContent>
    </Select>
  );

  if (isLastAdmin) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>{trigger}</span>
          </TooltipTrigger>
          <TooltipContent>
            Board must have at least one admin
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return trigger;
}

// ---------------------------------------------------------------------------
// TrashIcon — inline SVG for the remove button
// ---------------------------------------------------------------------------

function TrashIcon(): React.ReactElement {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 6h18" />
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}
